import { Flex, TextV2 } from '@withjoy/joykit';
import { Car, MapMarker } from '@withjoy/joykit/icons';
import React from 'react';
import { WalkIcon } from '@assets/icons';
import { Coordinates, AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { distanceInMiles, getWalkingAndDrivingDistance } from '@shared/utils/distanceInMiles';
import { useTranslation } from '@shared/core';
import { Skeleton } from '@shared/components/Skeleton';
import { WALKING_TIME_THRESHOLD_MINUTES } from '@apps/accommodations/constants';

interface TravelLogisticsProps {
  useShortWording?: boolean;
  venueCoordinates: Coordinates | null;
  listing: AccommodationListingDTO;
}

const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection={{ _: 'row', md: 'column' }}
      flexWrap={'wrap'}
      alignItems={{ _: 'center', md: 'start' }}
      columnGap={4}
      rowGap={1}
    >
      {children}
    </Flex>
  );
};

const Row = ({ icon, children }: { children: React.ReactNode; icon: JSX.Element }) => {
  return (
    <TextV2
      typographyVariant="label2"
      display="inline-flex"
      alignItems="center"
      columnGap={'2px'}
    >
      {icon} {children}
    </TextV2>
  );
};

const TravelLogistics = (props: TravelLogisticsProps) => {
  const { t } = useTranslation('accommodationsConsole');
  const rowTrans = t('listingsPanel', 'travelLogistics');
  const { venueCoordinates, listing, useShortWording } = props;
  const distanceFromTheVenue = distanceInMiles({ lat: venueCoordinates?.latitude, lng: venueCoordinates?.longitude }, listing.location)?.toFixed(1);
  if (!distanceFromTheVenue) return null;
  const travelTimes = getWalkingAndDrivingDistance(parseFloat(distanceFromTheVenue));

  return (
    <Container>
      <Row icon={<MapMarker size={16} />}>
        {useShortWording ? rowTrans.distanceFromVenueShort({ distanceMi: distanceFromTheVenue }) : rowTrans.distance({ distanceMi: distanceFromTheVenue })}
      </Row>
      {travelTimes.walking < WALKING_TIME_THRESHOLD_MINUTES ? (
        <Row icon={<WalkIcon />}>
          {useShortWording ? rowTrans.walkingDistanceShort({ walkingTime: travelTimes.walking }) : rowTrans.walkingDistance({ walkingTime: travelTimes.walking })}
        </Row>
      ) : (
        <Row icon={<Car size={16} />}>
          {useShortWording ? rowTrans.drivingDistanceShort({ drivingTime: travelTimes.driving }) : rowTrans.drivingDistance({ drivingTime: travelTimes.driving })}
        </Row>
      )}
    </Container>
  );
};

TravelLogistics.Skeleton = () => {
  return (
    <Container>
      <Skeleton
        width={130}
        height={18}
      />
      <Skeleton
        width={75}
        height={18}
      />
      <Skeleton
        width={80}
        height={18}
      />
    </Container>
  );
};

export { TravelLogistics };
